import { FolderIcon, TagIcon } from '@heroicons/react/outline';
import { OAuthError, patchData, postData } from '../backend';
import { SourceField, Workflow } from '../interfaces';
import { useEffect, useRef, useState } from 'react';
import { Popover } from '@headlessui/react';
import { createPortal } from 'react-dom';
import { useModal } from '../contexts';
import Modal from './Modal';
import { KeyedMutator } from "swr";
import { useTranslation } from 'react-i18next';
import ToggleButton from './ToggleButton';
import OAuthModalMicrosoft from './OAuthModalMicrosoft';
import useUser from '../hooks/useUser';
import MicrosoftFileBrowser from './MicrosoftFileBrowser';
import * as graph from '../microsoftGraph';

interface Props {
  workflow: Workflow;
  mutateWorkflow: KeyedMutator<Workflow>;
}

function WorkflowOutputOneDriveModal(props: Props) {
  const { user } = useUser();
  const { workflow, mutateWorkflow } = props;
  const { openModal, closeModal } = useModal();
  const { t } = useTranslation();

  const [isSaving, setIsSaving] = useState(false);

  const [microsoftOauthUrl, setMicrosoftOauthUrl] = useState<string>();
  const [onedriveScopeProvided, setOnedriveScopeProvided] = useState<boolean>();
  const [accessToken, setAccessToken] = useState<string>();

  const tagButtonRef = useRef<HTMLButtonElement>(null);
  const [onedriveDisabled, setOnedriveDisabled] = useState<boolean>(workflow.onedriveDisabled);
  const [onedriveFolderPath, setOnedriveFolderPath] = useState<string>(workflow.onedriveFolderPath ?? "");
  const [onedriveFolderDriveId, setOnedriveFolderDriveId] = useState<string>(workflow.onedriveDriveId ?? "");
  const [driveName, setDriveName] = useState<string>(workflow.onedriveDriveName ?? "");

  function updateWorkflow() {
    const updates: Record<string, any> = { onedriveDisabled, onedriveFolderPath };
    if (onedriveFolderDriveId) {
      updates.onedriveDriveId = onedriveFolderDriveId;
    }
    if (driveName) {
      updates.onedriveDriveName = driveName;
    }

    setIsSaving(true);
    patchData(`/workflows/${workflow.id}/`, updates)
      .then(() => mutateWorkflow())
      .then(() => closeModal())
      .catch(console.error)
      .finally(() => setIsSaving(false));
  }

  function toggleOnedriveDisabled(value: boolean) {
    if (microsoftOauthUrl)
      return openModal(<OAuthModalMicrosoft oauthUrl={microsoftOauthUrl} onClose={() => openModal(<WorkflowOutputOneDriveModal {...props} />)} />);

    setOnedriveDisabled(!value);
  }

  useEffect(() => {
    if (onedriveScopeProvided !== undefined)
      return;

    postData("/microsoft/access-token/?scopes=Files.ReadWrite.All")
      .then((token) => {
        setMicrosoftOauthUrl(undefined);
        setAccessToken(token);

        setOnedriveScopeProvided(true);
        setOnedriveDisabled(workflow.onedriveDisabled);
      })
      .catch(error => {
        setOnedriveDisabled(true);
        setOnedriveScopeProvided(false);

        if (error instanceof OAuthError)
          setMicrosoftOauthUrl(error.oauthUrl);
        else
          console.error(error);
      });

  }, [onedriveScopeProvided, workflow.onedriveDisabled]);

  function handleBrowseFolder() {
    openModal(
      <MicrosoftFileBrowser
        mode="folder"
        title={t("Select Output Folder")}
        onSelect={async (folderId: string, driveId?: string) => {
          let resolvedFolderPath = onedriveFolderPath;
          let resolvedDriveName = "";

          if (driveId) {
            setOnedriveFolderDriveId(driveId);
          }

          // Resolve the folder path and drive name from the Graph API
          if (accessToken && driveId) {
            try {
              resolvedFolderPath = await graph.getDriveItemPath(accessToken, driveId, folderId);
              setOnedriveFolderPath(resolvedFolderPath);
            } catch {
              // keep existing path
            }
            try {
              const drive = await graph.getDrive(accessToken, driveId);
              resolvedDriveName = drive.name;
              setDriveName(resolvedDriveName);
            } catch {
              // keep empty
            }
          }

          // Save selections to the workflow, then re-open this modal
          const updates: Record<string, any> = { onedriveFolderPath: resolvedFolderPath };
          if (driveId) updates.onedriveDriveId = driveId;
          if (resolvedDriveName) updates.onedriveDriveName = resolvedDriveName;

          patchData(`/workflows/${workflow.id}/`, updates)
            .then((updatedWorkflow) => {
              mutateWorkflow();
              setTimeout(() => openModal(
                <WorkflowOutputOneDriveModal workflow={updatedWorkflow} mutateWorkflow={mutateWorkflow} />
              ), 100);
            })
            .catch(() => {
              setTimeout(() => openModal(
                <WorkflowOutputOneDriveModal workflow={workflow} mutateWorkflow={mutateWorkflow} />
              ), 100);
            });
        }}
      />
    );
  }

  return (
    <Modal
      title={t("Output Folder")}
      secondaryButtonProps={{
        text: t("Cancel"),
        onClick: closeModal,
      }}
      primaryButtonProps={{
        text: t("Save"),
        onClick: updateWorkflow,
        disabled: isSaving,
      }}
    >
      <div className="text-gray-600 font-gilroy font-semibold flex items-center justify-between">
        {t("Save to OneDrive/SharePoint")}
        {(user?.authProvider !== "MICROSOFT" || onedriveDisabled) &&
          <ToggleButton value={!onedriveDisabled} onChange={toggleOnedriveDisabled} disabled={onedriveScopeProvided === undefined} />
        }
      </div>
      <div className='text-sm font-semibold text-gray-500 -mt-2'>
        {t("All files that are generated with a Microsoft output type will be automatically saved to your OneDrive. You can specify which folder these files are saved to using template tags or direct names.")}
      </div>

      {!onedriveDisabled && (
        <div className="flex items-center gap-2">
          <button
            className="btn btn-white flex items-center gap-2 text-sm flex-shrink-0"
            onClick={handleBrowseFolder}
            disabled={!onedriveScopeProvided}
          >
            <FolderIcon className="w-4 h-4" />
            {t("Browse Folders")}
          </button>
          {driveName && (
            <span className="text-sm text-gray-500 font-semibold truncate">
              {driveName}
            </span>
          )}
        </div>
      )}

      <div className='relative flex items-center w-full'>
        <div className='border-2 flex items-center roudned w-full text-sm font-semibold font-gilroy rounded'>
          <div className='flex-shrink-0 bg-gray-200 p-2'>
            {driveName || "OneDrive"}
          </div>
          <div className='mx-1'>
            /
          </div>
          <input type="text" className='w-full h-full outline-none pr-2' placeholder='{{Template Tag}} / Folder Name' value={onedriveFolderPath} onChange={e => setOnedriveFolderPath(e.currentTarget.value)} disabled={onedriveDisabled} />
          {!onedriveDisabled &&
            <Popover className="relative right-1 top-px">
              {({ open }) => {
                const rect = tagButtonRef.current?.getBoundingClientRect();
                return (
                  <>
                    <Popover.Button ref={tagButtonRef} className={`relative border rounded-md py-1 px-1 ml-2 bg-white border-blue`}>
                      <TagIcon className="w-4 h-4 cursor-pointer text-blue" />
                    </Popover.Button>
                    {createPortal(
                      <Popover.Panel className="fixed z-[100] flex flex-col max-h-52 w-40 bg-white border rounded shadow overflow-y-auto" style={{ top: (rect?.bottom ?? 0) + 4, left: (rect?.right ?? 0) - 160 }}>
                        {workflow.source?.sourceFields.map((sourceField: SourceField) => (
                          <Popover.Button key={sourceField.id} className="text-left text-sm border-b border-gray-400 last:border-none px-2 py-1 hover:bg-blue hover:bg-opacity-10 cursor-pointer" onClick={() => setOnedriveFolderPath(s => s + "{{" + sourceField.fieldName + "}}")}>
                            {sourceField.fieldName}
                          </Popover.Button>
                        ))}
                      </Popover.Panel>,
                      document.body
                    )}
                  </>
                );
              }}
            </Popover>
          }
        </div>
      </div>
      {!onedriveDisabled && (
        <div className='text-xs font-semibold text-gray-400'>
          {t("Browse to select a base folder, then optionally add {{Template Tags}} for dynamic sub-folders.")}
        </div>
      )}
    </Modal>
  );
}

export default WorkflowOutputOneDriveModal;
